import React, { useEffect, useMemo, useState } from "react";
import styles from "./ContractInformationEditor.module.css";
import { Row, Col } from "react-bootstrap";
import { ContractInformationEditorProps } from "./ContractInformationEditor.utils";
import { FieldProps } from "../../../../../Reusables/components/InputFieldsController/InputFieldsController.utils";
import CygnusForm from "../../../../../Reusables/components/CygnusForm/CygnusForm";
import * as Icon from "react-bootstrap-icons";
import { ThreeCircles } from "react-loader-spinner";
import CygnusSwitch from "../../../../../Reusables/components/SliderInput/SliderInput";
import ContentBlockEditorModal from "../../../ContentBlocksPage/components/ContentBlockEditorModal/ContentBlockEditorModal";
import { RichText } from "../../../../../pages/cygnus_gpt/CygnusGptDashboard/components/RichText/RichText";


/**
 * ContractInformationEditor Component
 *
 * Combined editor showing:
 * 1. Preview button row
 * 2. Cygnus form (contract info fields)
 * 3. Content blocks list
 */
const ContractInformationEditor: React.FC<ContractInformationEditorProps> = ({
  sharedData,
  setSharedData,
  onValidationChange,
  lastFocusedFieldRef,
  prevFocusedFieldRef,
}) => {
  const [previewBlock, setPreviewBlock] = React.useState({
    show: false,
    id: "",
    name: "",
    value: "",
  });

  const selectedClientId = sharedData?.selectedClientMasterId;
  const clientFieldData = sharedData?.contractInfoEditorData?.[selectedClientId];
  const contentBlocks =
    sharedData?.updatedContentBlocks || sharedData?.templateFieldsMapping?.ContentBlocks || [];

  const isFormReady = useMemo(() => Array.isArray(clientFieldData), [clientFieldData]);

  const handleFieldChange = (updatedFields: FieldProps[]) => {
    console.log("[Editor] onFieldsChange", { selectedClientId, updatedFields });
    if (!selectedClientId) return;
    setSharedData((prev) => ({
      ...prev,
      contractInfoEditorData: {
        ...prev.contractInfoEditorData,
        [selectedClientId]: updatedFields,
      },
    }));
  };

  const handleToggleBlock = (blockId: string | number, newValue: boolean) => {
    const updatedBlocks = contentBlocks.map((block: any) =>
      block.id === blockId ? { ...block, enabled: newValue } : block
    );
    setSharedData((prev: any) => ({
      ...prev,
      updatedContentBlocks: updatedBlocks,
    }));
  };

  const clientOptions = sharedData.clientMasterIds.map((id: number) => {
    const client = sharedData.allClientsData?.find((c: any) => c.ClientMasterId === id);
    return { value: id, label: client ? client["Client Name"] : `Client ${id}` };
  });

  useEffect(() => {
    const handleFocus = (e: FocusEvent) => {
      const input = e.target as HTMLInputElement;
      if (input?.id && lastFocusedFieldRef?.current !== undefined) {
        if (prevFocusedFieldRef && prevFocusedFieldRef.current !== undefined) {
          prevFocusedFieldRef.current = lastFocusedFieldRef.current;
        }
        lastFocusedFieldRef.current = input.id;
      }
    };
    const formEl = document.getElementById("cygnus-contract-form");
    formEl?.addEventListener("focusin", handleFocus);
    return () => formEl?.removeEventListener("focusin", handleFocus);
  }, [lastFocusedFieldRef, prevFocusedFieldRef]);

  // Loading state for form
  if (!isFormReady) {
    return (
      <div className={styles.fullWidthContainer}>
        <ThreeCircles height="50" width="50" color="#4fa94d" visible />
        <div className={styles.customGridLoaderText}>Loading your form</div>
      </div>
    );
  }

  if (clientFieldData.length === 0) {
    return <>This template has no engagement form connected. Please create one to continue.</>;
  }

  const handleContentBlockHide = () => {
    setPreviewBlock({
      show: false,
      id: "",
      name: "",
      value: "",
    });
  }

  return (
    <>
      {previewBlock.show &&
      <ContentBlockEditorModal
          mode={"view"}
          contentBlockId={previewBlock.id}
          blockName={previewBlock.name}
          blockData={previewBlock.value}
          active={"Y"}
          status={"Active"}
          onHide={handleContentBlockHide}
        />
      }
      <Row className={`no-gutter ${styles.sectionCard}`}>
        <h5 className={styles.sectionTitle}>Client Information</h5>
        <div className={styles.sectionBody}>
          <CygnusForm
            formId="cygnus-contract-form"
            fields={clientFieldData}
            onFieldsChange={handleFieldChange}
            onValidationChange={onValidationChange}
          />
        </div>
      </Row>

      {contentBlocks.length !== 0 && (
        <Row className={`no-gutter ${styles.sectionCard}`}>
          <h5 className={styles.sectionTitle}>Content Blocks</h5>
          <div className={styles.sectionBody}>
            <Col md={12} className={styles.contentBlocksList}>
              {contentBlocks.map((block: any) => {
                return (
                  <div
                    key={block.id}
                    className={`${styles.contentBlockCard} ${block.enabled ? styles.enabledBlock : ""}`}
                  >
                    <div className={styles.contentBlockHeader}>
                      <div className={styles.contentBlockInfo}>
                        <strong className={styles.blockName}>{block.name}</strong>
                        {block.hasInstructions && (
                          <span className={styles.instructionsIndicator} title="Instructions available">
                            <Icon.JournalText className={styles.instructionsIcon} />
                          </span>
                        )}
                        <Icon.Eye
                          className={styles.previewIcon}
                          title="Preview Content Block"
                          style={{
                            marginLeft: 10,
                            cursor: "pointer",
                            fontSize: 18,
                          }}
                          onClick={() =>
                            setPreviewBlock({
                              show: true,
                              id: block.id,
                              name: block.name,
                              value: block.value,
                            })
                          }
                        />
                      </div>
                      <CygnusSwitch
                        field={{
                          id: `content-block-${block.id}`,
                          type: "text",
                          value: block.enabled,
                          label: "Include",
                          textStyle: { fontSize: 12 },
                          show: true,
                        }}
                        handleChange={(fieldId, e) => handleToggleBlock(block.id, e.target.checked)}
                        handleBlur={() => {}}
                        handleSelect={() => {}}
                      />
                    </div>
                    {block.content && (
                      <div className={styles.blockPreview}>
                        <span>
                          <RichText htmlOrText={block.content.length > 200 ? block.content.slice(0, 200) + "..." : block.content} />
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </Col>
          </div>
        </Row>
      )}
    </>
  );
};

export default ContractInformationEditor;
